<template>
  <div>
    <v-container fluid>
      <v-row dense class="pb-1">
        <v-col cols="2">
          <v-select label="Order by" dense outlined
                    :items="[{text:'Customer',value:'customer'},{text:'Shipment zone',value:'zone'},{text:'Postal code',value:'postalCode'},{text:'Date',value:'date'}]"
                     v-model="orderBy" hide-details @change="refresh()"/>
        </v-col>
        <v-col cols="2"><v-switch label="Ascending" v-model="ascending" dense hide-details @change="refresh()" class="mt-1 ml-2"/></v-col>
        <v-col cols="2">
          <v-autocomplete label="Product units" dense outlined :items="unitsList" v-model="units" hide-details @change="refresh()"/>
        </v-col>
        <v-col cols="3">
          <v-autocomplete label="Show" dense outlined v-model="show" hide-details @change="refresh()"
                          :items="[{text:'Postal code and Shipping zone',value:'both'},
                                   {text:'Postal code',value:'postal'},
                                   {text:'Shipping zone',value:'zone'}]" />
        </v-col>
        <v-col class="text-right pr-2">
          <v-btn icon @click="printHtml('Undelivered shipments',report)"><v-icon>mdi-printer</v-icon></v-btn>
          <v-tooltip bottom open-delay="1000" v-if="company.allowCSVExport">
            <template v-slot:activator="{on}">
              <v-btn icon @click="exportCSV" v-on="on"><v-icon>mdi-download</v-icon></v-btn>
            </template>
            Export as CSV file
          </v-tooltip>
        </v-col>
      </v-row>
    </v-container>

    <v-card class="mx-4 pa-4 overflow-y" height="calc(100vh - 184px)">
      <span v-html="report"/>
    </v-card>
  </div>
</template>

<script>
import fileSaver from 'file-saver';
import printHtml from '@/mixins/printHtml'
import createHierarchyList from '@/mixins/createHierarchyList'

export default {
  name: 'ReportsSalesUndelivered',
  mixins: [printHtml,createHierarchyList],
  data() {
    return {
      orderBy: 'zone',
      ascending: true,
      unitsList: [{text:'bottles',value:'bottles'},{text:'units',value:'sellingUnits'},
                  {text:'cases',value:'cases'},{text:'pallets',value:'pallets'},
                  {text:'litres',value:'litres'},{text:'laa',value:'laa'}],
      units: 'bottles',
      show: 'both',
      loading: true,
      previousCustomer: null,
      totalValue: 0,
      shipments: [],
      report: ''
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    }
  },
  async created() {
    this.units = this.$route.query.units ?? 'bottles';
    this.$emit('name', 'Undelivered shipments');

    const results = await this.$apiCall(`query ReportsSalesUndelivered {
                                            shipmentsUndelivered {
                                              shipmentId, customer, date, invoiceNumber, postalCode, zone, shipToAddress, phone
                                              items {
                                                name,product,units,precision
                                                quantity,bottles,sellingUnits,cases,pallets,litres,laa
                                              }
                                            }
                                          }`,
                                        {});
    if (!results.error)
      this.shipments = results.data.shipmentsUndelivered;

    this.refresh();
  },
  methods: {
    printQuantity(line) {
      return `<td class="text-right">
        ${line.product ?
          `${this.$number(line[this.units],line.precision)} ${this.unitsList.find(unit => unit.value === this.units).text}`
          :
          `${this.$number(line.quantity,line.precision)} ${line.units}`}
        </td>`;
    },
    async refresh() {
      this.loading = true;

      this.shipments = this.shipments.sort((a,b) => {
        return (a[this.orderBy] > b[this.orderBy] ? 1 : -1) * (this.ascending ? 1 : -1);
      });

      const lines = [
                  `<style>
                    @page {size:landscape}
                    .report {font-family: Roboto, sans-serif; width:100%;border-spacing:0;font-size:12px}
                    .report th {height:36px;padding:0 16px;vertical-align:middle}
                    .report .spacer {height:18px}
                    .report td {height:36px;padding:0 16px;vertical-align:top}
                    .report .title {font-size:18px;text-transform:uppercase}
                    .report .sup-title {font-weight:normal;font-size:22px}
                    .report .sub-title {font-weight:normal;height:inherit;padding-bottom:20px;font-size:14px}
                    .report .header th {border-bottom:1px solid #000;margin-bottom:20px}
                    .report .text-left {text-align:left}
                    .report .text-right {text-align:right}
                    .report .indent {text-indent:20px}
                    .report .font-weight-bold {font-weight:bold}
                    .report .total-row {font-weight:bold}
                  </style>
                   <table class="report">
                     <thead>
                        <tr>
                          <th colspan="6" class="sup-title">${this.company.name}</th>
                        </tr>
                        <tr>
                          <th colspan="6" class="title">
                            Undelivered shipments
                          </th>
                        </tr>
                        <tr class="header">
                          <th width="25%" class="text-left">Customer</th>
                          ${this.show === 'both' || this.show === 'zone' ? '<th width="10%" class="text-left">Zone</th>' : ''}
                          ${this.show === 'both' || this.show === 'postal' ? '<th width="10%" class="text-left">Postal code</th>' : ''}
                          <th width="15%" class="text-left">Shipment date</th>
                          <th width="10%" class="text-left">Invoice</th>
                          <th width="25%" class="text-left">Item</th>
                          <th width="15%" class="text-right">Quantity</th>
                        </tr>
                      </thead>
                      <tbody>`
      ];

      for (const shipment of this.shipments) {
        const itemsCount = shipment.items.length;
        lines.push(`<tr>
                      <td rowspan="${itemsCount}">
                        <strong>${shipment.customer}</strong>
                        <div>
                          ${shipment.shipToAddress.replace(/\n/gi,'<br/>')}<br/>&nbsp;
                        </div>
                      </td>
                      ${this.show === 'both' || this.show === 'zone' ? `<td rowspan="${itemsCount}"><strong>${shipment.zone}</strong></td>` : ''}
                      ${this.show === 'both' || this.show === 'postal' ? `<td rowspan="${itemsCount}"><strong>${shipment.postalCode}</strong></td>` : ''}
                      <td rowspan="${itemsCount}"><strong>${this.$formatDateTime(shipment.date)}</strong></td>
                      <td rowspan="${itemsCount}"><strong>${shipment.invoiceNumber ?? ''}</strong></td>
                      <td>${shipment.items[0].name}</td>
                      ${this.printQuantity(shipment.items[0])}
                    </tr>`);
        for (let i = 1; i < itemsCount; i++)
          lines.push(`<tr>
                        <td>${shipment.items[i].name}</td>
                        ${this.printQuantity(shipment.items[i])}
                      </tr>`);
      }



      lines.push(`</tbody>`);
      lines.push(`</table>`);
      this.report = lines.join('');
      this.loading = false;
    },

    exportCSV() {
      const lines = [ `"${this.company.name}",,,,,,,,`,
                      `"Undelivered shipments",,,,,,,,`,
                      `"Customer","Address","Zone","Postal Code","Phone","Date","Invoice","Item","Quantity"`
                    ];

      for (const shipment of this.shipments)
        for (const item of shipment.items) {
          const quantity = item.product ? `${this.$number(item[this.units],item.precision)}` :
                                          `${this.$number(item.quantity,item.precision)}`;
          lines.push(`"${shipment.customer}","${shipment.shipToAddress}","${shipment.zone}","${shipment.postalCode}","${shipment.phone}","${shipment.date}","${shipment.invoiceNumber??''}","${item.name}","${quantity}"`);
        }

      // Write CSV file
      const blob = new Blob([lines.join('\r\n')], {type: "text/plain;charset=utf-8"});
      fileSaver.saveAs(blob, 'Undelivered shipments.csv');
    }
  }
}
</script>